import React, { useState, useEffect, useRef } from 'react';
import { usePost } from '../API';
import { 
  useToast, 
  Button, 
  Heading, 
  Alert, 
  CodeDisplay,
  TextArea,
  Input,
  Card
} from '@aidkitorg/component-library';

interface DistroReviewSummaryProps {
  reviewData: {
    survey?: any;
    currentSurvey?: any;
    diff?: string;
    error?: string;
    surveyName?: string;
    status?: string;
    publishedAt?: string;
    publishedBy?: string;
    description?: string;
    title?: string;
  } | null;
  reviewId: string;
  isClosing: boolean;
  isPublishing: boolean;
  onCloseReviewClick: () => void;
  onUpdate?: () => void;
}

export default function DistroReviewSummary({ 
  reviewData, 
  reviewId, 
  isClosing, 
  isPublishing, 
  onCloseReviewClick,
  onUpdate 
}: DistroReviewSummaryProps) {
  const patchDistroReview = usePost('/distro-review/patch');
  const [description, setDescription] = useState('');
  const [title, setTitle] = useState('');
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [isUpdatingDescription, setIsUpdatingDescription] = useState(false);
  const [isUpdatingTitle, setIsUpdatingTitle] = useState(false);
  const { toast } = useToast();

  const titleInputRef = useRef<HTMLInputElement>(null);
  const descriptionTextAreaId = 'description-textarea';

  const isPendingReview = reviewData?.status === 'review_pending';

  useEffect(() => {
    if (!isEditingDescription) setDescription(reviewData?.description || '');
    if (!isEditingTitle) setTitle(reviewData?.title || '');
  }, [reviewData?.description, reviewData?.title, isEditingDescription, isEditingTitle]);

  useEffect(() => {
    if (isEditingTitle && titleInputRef.current) {
      titleInputRef.current.focus();
    }
  }, [isEditingTitle]);

  useEffect(() => {
    if (isEditingDescription) {
      const textarea = document.getElementById(descriptionTextAreaId) as HTMLTextAreaElement;
      if (textarea) {
        textarea.focus();
      }
    }
  }, [isEditingDescription]);

  const handleEditDescriptionClick = () => {
    setIsEditingDescription(true);
  };

  const handleCancelDescriptionEdit = () => {
    setDescription(reviewData?.description || '');
    setIsEditingDescription(false);
  };

  const handleSaveDescription = async () => {
    if (!reviewId) return;
    
    setIsUpdatingDescription(true);
    try {
      const result = await patchDistroReview({ id: reviewId, description });
      if (result.error) {
        toast({
          description: result.error,
          variant: 'error'
        });
      } else {
        toast({
          description: 'Description updated successfully',
          variant: 'success'
        });
        setIsEditingDescription(false);
        // Trigger refresh from parent to get updated data from backend
        onUpdate?.();
      }
    } catch (error) {
      console.error('Error updating description:', error);
      toast({
        description: 'Failed to update description',
        variant: 'error'
      });
    }
  };

  const handleEditTitleClick = () => {
    setIsEditingTitle(true);
  };

  const handleCancelTitleEdit = () => {
    setTitle(reviewData?.title || '');
    setIsEditingTitle(false);
  };

  const handleSaveTitle = async () => {
    if (!reviewId) return;
    
    setIsUpdatingTitle(true);
    try {
      const result = await patchDistroReview({ id: reviewId, title });
      if (result.error) {
        toast({
          description: result.error,
          variant: 'error'
        });
      } else {
        toast({
          description: 'Title updated successfully',
          variant: 'success'
        });
        setIsEditingDescription(false);
        // Trigger refresh from parent to get updated data from backend
        onUpdate?.();
      }
    } catch (error) {
      console.error('Error updating title:', error);
      toast({
        description: 'Failed to update title',
        variant: 'error'
      });
    }
  };

  const handleTitleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !isUpdatingTitle) {
      e.preventDefault();
      handleSaveTitle();
    }
  };

  return (
    <>
      <div className="flex flex-row justify-between items-start mb-2">
        <Heading level="h2" className="text-lg font-semibold">Summary</Heading>
        <Button
          onClick={onCloseReviewClick}
          isDisabled={isClosing || isPublishing}
          loading={isClosing}
          variant="danger"
        >
          Close Review
        </Button>
      </div>
      
      {/* Title Section for Pending Reviews */}
      {isPendingReview && (
        <div className="mb-6 w-full">
          {isEditingTitle ? (
            <div className="flex flex-row items-center gap-3 w-full">
              <div className="flex-1 w-full">
                <Input
                  label="Review Title"
                  showLabel={false}
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  onKeyDown={handleTitleKeyDown}
                  placeholder="Add a short title for this review..."
                  disabled={isUpdatingTitle}
                  ref={titleInputRef}
                />
              </div>
              <div className="flex flex-row justify-end items-center gap-3">
                <Button
                  onClick={handleCancelTitleEdit}
                  variant="secondary"
                  isDisabled={isUpdatingTitle}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSaveTitle}
                  loading={isUpdatingTitle}
                  variant="primary"
                  isDisabled={isUpdatingTitle}
                >
                  Save Title
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex flex-row justify-between items-center w-full gap-3">
              
              {reviewData?.title ? (
                <div className="flex items-center text-center border border-gray-200 rounded-lg p-2 w-full flex-1">
                  <Heading level="h3" className="text-lg font-semibold text-gray-900 mb-0">{reviewData.title}</Heading>
                </div>
              ) : (
                <p className="text-sm text-gray-500 italic text-center mb-0">No title added yet</p>
              )}
              <div className="items-center">
                <Button
                  onClick={handleEditTitleClick}
                  variant="secondary"

                >
                  Edit Title
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
      
      {/* Display title for non-editable states */}
      {!isPendingReview && reviewData?.title && (
        <Card className="mb-6">
          <Heading level="h3" className="text-lg font-semibold text-gray-900">{reviewData.title}</Heading>
        </Card>
      )}

      {/* Description Section for Pending Reviews */}
      {isPendingReview && (
        <div className="mb-8">
          <div className="flex justify-between items-end">
            <Heading level="h3" className="text-base font-semibold text-gray-900 mb-2">Description</Heading>
            {!isEditingDescription && (
              <Button
                onClick={handleEditDescriptionClick}
                variant="secondary"
                className="mb-2"
              >
                Edit Description
              </Button>
            )}
          </div>
          
          {isEditingDescription ? (
            <div className="space-y-4">
              <TextArea
                label="Review Description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Add a description for this review..."
                rows={4}
                className="w-full"
                disabled={isUpdatingDescription}
                id={descriptionTextAreaId}
              />
              <div className="flex justify-end gap-3">
                <Button
                  onClick={handleCancelDescriptionEdit}
                  variant="secondary"
                  isDisabled={isUpdatingDescription}
                  size="sm"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSaveDescription}
                  loading={isUpdatingDescription}
                  variant="primary"
                  isDisabled={isUpdatingDescription}
                  size="sm"
                >
                  Save Description
                </Button>
              </div>
            </div>
          ) : (
            <div className="relative">
              {reviewData?.description ? (
                <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow duration-200">
                  <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">{reviewData.description}</p>
                </div>
              ) : (
                <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                  <p className="text-sm text-gray-500 italic">No description added yet. Click "Edit Description" to add one.</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}
      
      {/* Display description for non-editable states */}
      {!isPendingReview && reviewData?.description && (
        <div className="mb-8">
          <Heading level="h3" className="text-base font-semibold text-gray-900 mb-4">Description</Heading>
          <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
            <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">{reviewData.description}</p>
          </div>
        </div>
      )}

      {reviewData?.diff ? (
        <div className="mb-6">
          <Heading level="h3" className="text-lg font-semibold text-gray-900 mb-4">Changes</Heading>
          <div className="border border-gray-200 rounded-lg overflow-hidden shadow-sm">
            <CodeDisplay
              code={reviewData.diff}
              language="text"
              className="whitespace-pre-wrap text-sm"
            />
          </div>
        </div>
      ) : (
        <Alert
          title="No Diff Available"
          variant="warning"
          className="mb-6"
        >
          No diff available. The proposed survey is identical to the current survey.
        </Alert>
      )}
    </>
  );
} 
